import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import uploadcare from 'uploadcare-widget';
import Button from 'partial/components/Button';
import { useResizeObserver } from '../hooks';

const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  // eslint-disable-next-line
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

function FormSignature({ name, onChange, onSetFieldValue, value }) {
  const ctxRef = useRef();
  const canvasRef = useRef();
  const containerRef = useRef();
  const [isEmtpy, setIsEmpty] = useState(true);
  const [penIsDown, setPenIsDown] = useState(false);
  const dimensions = useResizeObserver(containerRef);
  const [uploadStatus, setUploadStatus] = useState('standy');

  const handleCreateImage = useCallback(() => {
    const b64data = canvasRef.current.toDataURL();
    const fileToUpload = dataURLtoFile(b64data, 'signature');
    const upload = uploadcare.fileFrom('object', fileToUpload, {
      publicKey: process.env.REACT_APP_UPLOADCARE_KEY,
    });

    upload.progress((uploadInfo) => setUploadStatus(uploadInfo.state));
    upload.done((fileInfo) => {
      const url = fileInfo?.cdnUrl;
      if (typeof onChange === 'function') {
        onChange((state) => ({
          ...state,
          [name]: url,
        }));
      }
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, url);
      }
    });
  }, [canvasRef, name, onChange, onSetFieldValue]);

  const getPos = useCallback(
    ({ clientX, clientY }) => {
      const { top, left } = canvasRef.current.getBoundingClientRect();
      const x = clientX - left;
      const y = clientY - top;
      return {
        x,
        y,
      };
    },
    [canvasRef]
  );

  const clear = useCallback(() => {
    const { width, height } = canvasRef.current.getBoundingClientRect();
    ctxRef.current.clearRect(0, 0, width, height);
    setIsEmpty(true);
    setUploadStatus('standy');
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: '',
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, '');
    }
  }, [ctxRef, name, onChange, onSetFieldValue]);

  const draw = useCallback(
    (x, y) => {
      ctxRef.current.lineTo(x, y);
      ctxRef.current.stroke();
      ctxRef.current.beginPath();
      ctxRef.current.moveTo(x, y);
    },
    [ctxRef]
  );

  const handlePenDown = (e) => {
    const { x, y } = getPos(e);
    draw(x, y);
    setPenIsDown(true);
    setIsEmpty(false);
  };

  const handlePenUp = useCallback(() => {
    setPenIsDown(false);
    ctxRef.current.beginPath();
  }, [ctxRef]);

  const handleMovePen = (e) => {
    if (!penIsDown) return;
    const { x, y } = getPos(e);
    draw(x, y);
  };

  const handleTouchMove = (e) => {
    if (!penIsDown) return;
    setIsEmpty(false);
    const { x, y } = getPos(e.touches[0]);
    draw(x, y);
  };

  useEffect(() => {
    setIsEmpty(true);
    canvasRef.current.width = dimensions?.width;
    canvasRef.current.height = dimensions?.height;
    ctxRef.current = canvasRef?.current?.getContext('2d');
    ctxRef.current.strokeStyle = 'black';
    ctxRef.current.lineWidth = '2';
    ctxRef.current.lineCap = 'round';
  }, [dimensions]);

  return (
    <div
      className="mt-4 bg-purple-50 rounded-lg w-full h-0 relative overflow-hidden border"
      style={{ paddingTop: '33%' }}
    >
      {value && (
        <div className="absolute inset-0 z-20 bg-white">
          <div className="absolute top-4 right-4">
            <Button
              size="sm"
              default
              className="ml-auto mt-4 mr-4"
              onClick={clear}
            >
              Sign Again
            </Button>
          </div>
          <img
            alt="signature"
            className="w-full h-full object-contain"
            src={value}
          />
        </div>
      )}
      {isEmtpy && uploadStatus === 'standy' && (
        <p className="absolute top-4 left-4 text-gray-500">
          Your signature here
        </p>
      )}
      {!isEmtpy && uploadStatus === 'standy' && (
        <div className="flex absolute space-x-2 top-4 right-4 z-10">
          <Button size="sm" default onClick={clear}>
            Clear
          </Button>
          <Button size="sm" success onClick={handleCreateImage}>
            Confirm
          </Button>
        </div>
      )}
      {!isEmtpy && ['uploading', 'uploaded'].includes(uploadStatus) && (
        <div className="absolute inset-0 mt-10 text-center">
          <div className="text-gray-500 text-xs uppercase tracking-wide">
            Uploading...
          </div>
          <div className="loaderBar w-32">
            <div className="loaderBarInner" />
          </div>
        </div>
      )}
      <div ref={containerRef} className="absolute inset-0">
        <canvas
          ref={canvasRef}
          onMouseDown={handlePenDown}
          onMouseUp={handlePenUp}
          onMouseMove={handleMovePen}
          onTouchStart={() => setPenIsDown(true)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handlePenUp}
          onMouseLeave={handlePenUp}
        />
      </div>
    </div>
  );
}

FormSignature.defaultProps = {
  onChange: false,
  onSetFieldValue: false,
};

FormSignature.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
};

export default FormSignature;
